/**
 * SidebarStep5 - BI Review + Causation Test (Historical BI Path)
 * 
 * This component shows the BI calculation results and causation test analysis.
 * Shows when calculation_mode === 'standard_bi' (default)
 * 
 * Features:
 *   - BI calculation results
 *   - Causation test (V-Test) results
 *   - Revenue/VP charts
 *   - Editable financial grid
 *   - K-1 partner summary
 * 
 * Preserves ALL functionality from Step5_BI_Review.tsx - only layout changes.
 */

import React, { useEffect, useState } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, LineChart, Line 
} from 'recharts';
import { 
  CheckCircle, XCircle, AlertCircle, TrendingDown, TrendingUp,
  DollarSign, Edit3, Eye, Users, Clock, Calculator, Loader2,
  FileText, AlertTriangle
} from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import type { QAItem } from './WizardSidebar';
import { useAuth } from '../../../contexts/TestAuthContext';
import EditableFinancialGrid from '../EditableFinancialGrid';
import K1PartnerSummary from '../K1PartnerSummary';
import PartialYearBanner from '../PartialYearBanner';
import { resolveCoverageYear } from '../../../utils/resolveCoverageYear';
import { FinancialValue } from '../../../components/shared/FinancialValue';
import { formatCurrency } from '../../../utils/financialFormat';

interface SidebarStep5Props {
  goToStep?: (step: number) => void;
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  onDataUpdate?: (data: any) => void;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  highestCompletedStep?: number;
  backendCompletionPercent?: number;
  claimId?: string;
}

interface BIResult {
  step1: {
    variable_profit_loss: number;
    benchmark_vp: number;
    coverage_vp: number;
  };
  step2: {
    incremental_loss: number;
    revenue_adjustment: number;
    variable_margin: number;
  };
  final_claim: number;
  causation: {
    test_passed: string;
    passed?: boolean;
    test_name?: string;
    qualifying_periods: string[];
    variance: number | null;
    threshold: number | null;
  };
  // Engine-truth per-year revenue + variable profit (deterministic backend
  // engine output). The "Revenue by Year" chart RENDERS this directly — no
  // frontend math. May be undefined/empty if the backend could not derive it.
  yearly_breakdown?: { year: number; revenue: number; variable_profit: number }[];
  event_type: string;
  event_display_name: string;
}

export default function SidebarStep5({
  onNext,
  onBack,
  goToStep,
  data,
  onDataUpdate,
  onSave,
  isSaving,
  lastSaved,
  highestCompletedStep,
  backendCompletionPercent,
  claimId
}: SidebarStep5Props) {
  const [result, setResult] = useState<BIResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeView, setActiveView] = useState<'preview' | 'editor' | 'k1summary'>('preview');
  const [reportId, setReportId] = useState<string | null>(null);
  const [dataCompleteness, setDataCompleteness] = useState<{complete: boolean, missing: string[]}>({complete: false, missing: []});
  const [editedFinancialData, setEditedFinancialData] = useState<any>(null);
  const [partialYearInfo, setPartialYearInfo] = useState<any>(null);  // Track partial year handling
  const [warnings, setWarnings] = useState<string[]>([]);  // Track warnings
  // SSoT: the persisted claim value the War Room shows (db.reports.result.
  // final_claim_amount), surfaced by the draft endpoint. Used only to
  // reconcile the live preview against the saved figure — never recomputed.
  const [savedClaim, setSavedClaim] = useState<number | null>(null);
  const { getToken } = useAuth();

  // Validate data completeness
  const validateDataCompleteness = () => {
    const missing: string[] = [];
    const financialData = data.financial_data || {};
    
    const calculateRequiredYears = () => {
      if (data.calculated_years && Array.isArray(data.calculated_years) && data.calculated_years.length > 0) {
        return data.calculated_years;
      }
      
      if (financialData) {
        const dataYears = Object.keys(financialData)
          .filter(key => key.startsWith('data_'))
          .map(key => parseInt(key.replace('data_', '')))
          .filter(y => !isNaN(y))
          .sort((a, b) => a - b);
        
        if (dataYears.length > 0) return dataYears;
      }
      
      // Last resort: derive coverage year from claim data (never today).
      const resolved = resolveCoverageYear(data);
      if (resolved !== null) {
        return [resolved - 3, resolved - 2, resolved - 1, resolved];
      }
      // No year resolvable — return [] so the validation shows a real
      // empty state instead of phantom future-year rows.
      return [];
    };
    
    const requiredYears = calculateRequiredYears();
    const missingYears: number[] = [];
    const minRequiredYears = Math.min(3, requiredYears.length);
    let yearsWithData = 0;
    
    requiredYears.forEach(year => {
      const yearKey = `data_${year}`;
      const yearData = financialData[yearKey];
      
      if (!yearData || !Array.isArray(yearData) || yearData.length === 0) {
        missingYears.push(year);
      } else {
        const hasRealRevenue = yearData.some((month: any) => (month.revenue || 0) > 0);
        if (!hasRealRevenue) {
          missingYears.push(year);
        } else {
          yearsWithData++;
        }
      }
    });
    
    if (yearsWithData < minRequiredYears) {
      missing.push(`Need at least ${minRequiredYears} years of financial data (have ${yearsWithData})`);
    }
    
    return { complete: missing.length === 0, missing };
  };

  // Run BI calculation
  useEffect(() => {
    const runCalculation = async () => {
      try {
        setLoading(true);
        setError('');
        
        const completeness = validateDataCompleteness();
        setDataCompleteness(completeness);
        
        if (!completeness.complete) {
          setLoading(false);
          return;
        }
        
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        const token = await getToken();
        
        const payload = {
          client_name: data.client_name,
          claimant_name: data.claimant_name,
          business_address: data.business_address,
          zone: data.zone,
          business_type: data.business_type,
          event_type: data.event_type,
          event_start_date: data.event_start_date,
          event_end_date: data.event_end_date,
          coverage_year: data.coverage_year,
          financial_data: data.financial_data,
          payroll_data: data.payroll_data || {},
          calculated_years: data.calculated_years || [],
          tax_data: data.tax_data || [],
        };

        // CRITICAL FIX: Use XMLHttpRequest instead of fetch() to avoid Clerk rrweb-recorder
        // "Response.clone: Body has already been consumed" error
        const validationResult = await new Promise<any>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${BACKEND_URL}/api/v1/reports/validate`);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const data = JSON.parse(xhr.responseText);
                resolve(data);
              } catch (e) {
                reject(new Error('Failed to parse validation response'));
              }
            } else {
              // Include actual backend error in the message for debugging
              let errorMsg = `Validation failed (HTTP ${xhr.status})`;
              try {
                const errData = JSON.parse(xhr.responseText);
                if (errData.detail) {
                  // Pydantic validation errors come as detail array
                  const detail = Array.isArray(errData.detail)
                    ? errData.detail.map((d: any) => `${d.loc?.join('.')}: ${d.msg}`).join('; ')
                    : String(errData.detail);
                  errorMsg = `Validation failed: ${detail}`;
                }
              } catch (_) { /* ignore parse error */ }
              console.error('🔴 Validate endpoint error:', xhr.status, xhr.responseText?.substring(0, 500));
              reject(new Error(errorMsg));
            }
          };
          
          xhr.onerror = () => reject(new Error('Network error during validation'));
          xhr.send(JSON.stringify(payload));
        });
        
        console.log('🔍 BI Validation Result:', validationResult);
        
        if (validationResult.report_id) {
          setReportId(validationResult.report_id);
        }
        
        // Capture partial year handling info and warnings
        if (validationResult.partial_year_handling) {
          console.log('📅 Partial year handling applied:', validationResult.partial_year_handling);
          setPartialYearInfo(validationResult.partial_year_handling);
        }
        if (validationResult.warnings && validationResult.warnings.length > 0) {
          console.log('⚠️ Warnings:', validationResult.warnings);
          setWarnings(validationResult.warnings);
        }
        
        // Backend returns result directly, not nested under bi_result
        if (validationResult.valid) {
          console.log('✅ Valid response - setting result');
          setResult(validationResult);
        } else if (validationResult.bi_result) {
          console.log('✅ Nested bi_result - setting result');
          setResult(validationResult.bi_result);
        } else {
          console.log('⚠️ Invalid response - no result set', validationResult);
          // Still set the result for debugging visibility
          if (validationResult.errors) {
            setError(validationResult.errors.join(', ') || 'Validation failed');
          }
        }
        
      } catch (err: any) {
        console.error('BI calculation error:', err);
        setError(err.message || 'Failed to calculate BI');
      } finally {
        setLoading(false);
      }
    };

    runCalculation();
  }, [data, getToken]);

  // SSoT reconciliation: fetch the persisted (War Room) claim value so BI
  // Review can show the SAME number the War Room shows. Read-only — the live
  // /validate result above stays the editable preview; this only surfaces the
  // saved report value (db.reports.result.final_claim_amount) the War Room
  // reads, exposed by the draft endpoint as `saved_final_claim_amount`.
  useEffect(() => {
    const id = claimId;
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
        const token = await getToken();
        const draft = await new Promise<any>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', `${BACKEND_URL}/api/v1/reports/drafts/${id}`);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try { resolve(JSON.parse(xhr.responseText)); }
              catch { reject(new Error('parse')); }
            } else { reject(new Error(`HTTP ${xhr.status}`)); }
          };
          xhr.onerror = () => reject(new Error('network'));
          xhr.send();
        });
        const v = draft?.saved_final_claim_amount;
        if (!cancelled && typeof v === 'number' && isFinite(v)) {
          setSavedClaim(v);
        }
      } catch {
        /* best-effort — no saved value to reconcile against */
      }
    })();
    return () => { cancelled = true; };
  }, [claimId, getToken]);

  // Handle next
  const handleNext = () => {
    onNext({
      ...data,
      bi_result: result,
      report_id: reportId,
    });
  };

  // Handle Quick Actions from sidebar
  const handleQuickAction = (action: string) => {
    console.log('⚡ Quick Action triggered (Step 5):', action);
    switch (action) {
      case 'rerun':
        // Re-run the BI calculation
        setLoading(true);
        setResult(null);
        // The useEffect will trigger runCalculation when loading changes
        setTimeout(() => {
          window.location.reload(); // Simple way to re-run - triggers fresh calculation
        }, 100);
        break;
      default:
        console.log('Unknown quick action:', action);
    }
  };

  // Prepare chart data from the deterministic BACKEND engine.
  // The backend (validate-report -> calculate_claim) emits `yearly_breakdown`
  // with the engine's REAL per-year revenue + variable profit. We RENDER it
  // directly — NO frontend math. The old client-side VP approximation
  // (revenue - cogs - payroll, with a `* 0.3` fabricated fallback) invented
  // numbers that disagreed with the engine and is removed. If yearly_breakdown
  // is missing/empty, render an empty chart rather than fabricating data.
  const chartData = (result?.yearly_breakdown ?? []).map(entry => ({
    year: String(entry.year),
    revenue: entry.revenue,
    vp: entry.variable_profit,
    label: String(entry.year),
  }));

  // Calculate completion
  const completionPercent = 85;
  const causationPassed = result?.causation?.test_passed && result.causation.test_passed !== 'No Test Passed';
  const grade = causationPassed ? 'A' : result ? 'B' : 'C';

  // Build sidebar sections
  const buildSections = () => [
    {
      id: 'causation',
      label: 'Causation Test',
      isActive: activeView === 'preview',
      isComplete: !!result?.causation,
      onClick: () => setActiveView('preview')
    },
    {
      id: 'bi',
      label: 'BI Results',
      isActive: false,
      isComplete: !!result?.final_claim,
      onClick: () => setActiveView('preview')
    },
    {
      id: 'editor',
      label: 'Edit Data',
      isActive: activeView === 'editor',
      isComplete: false,
      onClick: () => setActiveView('editor')
    },
    {
      id: 'k1',
      label: 'K-1 Summary',
      isActive: activeView === 'k1summary',
      isComplete: (data.tax1065Files?.length || 0) > 0,
      onClick: () => setActiveView('k1summary')
    }
  ];

  // Build QA items
  // QAItem.status canonical literals: 'missing' | 'warning' | 'complete'.
  const buildQAItems = (): QAItem[] => {
    const items: QAItem[] = [];

    if (result) {
      items.push({ id: 'calc', label: 'BI calculated', status: 'complete' });
    }

    if (causationPassed) {
      items.push({ id: 'causation', label: 'Causation passed', status: 'complete' });
    } else if (result) {
      items.push({ id: 'causation', label: 'Causation failed', status: 'warning' });
    }

    return items;
  };

  // Loading state
  if (loading) {
    return (
      <WizardV2Layout
        currentStep={5}
        highestCompletedStep={highestCompletedStep}
        stepTitle="BI Review"
        stepSubtitle="Calculating..."
        completionPercent={backendCompletionPercent ?? 80}
        calculationMode={data.calculation_mode || 'standard_bi'}
        grade="C"
        sections={[]}
        qaItems={[]}
        onBack={onBack}
        onNext={() => {}}
        showNext={false}
        claimId={claimId}
        goToStep={goToStep}
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 animate-spin" style={{ color: 'var(--color-brand)' }} />
            <p className="cs-h3">Running BI Calculation...</p>
            <p className="cs-meta">Analyzing financial data and causation</p>
          </div>
        </div>
      </WizardV2Layout>
    );
  }

  // Data incomplete state
  if (!dataCompleteness.complete) {
    return (
      <WizardV2Layout
        currentStep={5}
        highestCompletedStep={highestCompletedStep}
        stepTitle="BI Review"
        stepSubtitle="Missing required data"
        completionPercent={backendCompletionPercent ?? 70}
        calculationMode={data.calculation_mode || 'standard_bi'}
        grade="D"
        sections={[]}
        qaItems={[{ id: 'data', label: 'Data incomplete', status: 'missing' }]}
        onBack={onBack}
        onNext={() => {}}
        showNext={false}
        claimId={claimId}
        goToStep={goToStep}
      >
        <div className="p-6 lg:p-8">
          <div className="max-w-2xl mx-auto text-center">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
            <h2 className="cs-h2 mb-4">Missing Required Data</h2>
            <p className="cs-body mb-6">
              The following data is required for BI calculation:
            </p>
            <div className="rounded-xl p-4 mb-6" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
              <ul className="space-y-2 text-left">
                {dataCompleteness.missing.map((item, idx) => (
                  <li key={idx} className="cs-body flex items-start gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                    <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <button
              onClick={() => goToStep ? goToStep(3) : onBack()}
              className="cs-body px-6 py-3 rounded-lg font-medium"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
            >
              ← Go Back to Financial Data
            </button>
          </div>
        </div>
      </WizardV2Layout>
    );
  }

  // Error state
  if (error && !result) {
    return (
      <WizardV2Layout
        currentStep={5}
        highestCompletedStep={highestCompletedStep}
        stepTitle="BI Review"
        stepSubtitle="Calculation error"
        completionPercent={backendCompletionPercent ?? 75}
        calculationMode={data.calculation_mode || 'standard_bi'}
        grade="D"
        sections={[]}
        qaItems={[{ id: 'error', label: 'Error occurred', status: 'warning' }]}
        onBack={onBack}
        onNext={() => {}}
        showNext={false}
        claimId={claimId}
        goToStep={goToStep}
      >
        <div className="p-6 lg:p-8">
          <div className="max-w-2xl mx-auto text-center">
            <XCircle className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-brand)' }} />
            <h2 className="cs-h2 mb-4">Calculation Error</h2>
            <p className="cs-body mb-6" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
            <button
              onClick={onBack}
              className="cs-body px-6 py-3 rounded-lg font-medium"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
            >
              ← Go Back
            </button>
          </div>
        </div>
      </WizardV2Layout>
    );
  }

  return (
    <WizardV2Layout
      currentStep={5}
      highestCompletedStep={highestCompletedStep}
      stepTitle="BI Review + Causation Test"
      stepSubtitle={result ? `Final Claim: ${formatCurrency(result.final_claim)}` : 'Review calculation results'}
      completionPercent={backendCompletionPercent ?? completionPercent}
      calculationMode={data.calculation_mode || 'standard_bi'}
      grade={grade}
      sections={buildSections()}
      qaItems={buildQAItems()}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      onNext={handleNext}
      claimId={claimId}
      nextLabel="Continue to Report"
      isNextDisabled={!result}
      showNext={true}
      onQuickAction={handleQuickAction}
    >
      <div className="p-6 lg:p-8 space-y-6">
        
        {/* View Tabs */}
        <div className="flex gap-2 pb-4" style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
          <button
            onClick={() => setActiveView('preview')}
            className="cs-body px-4 py-2 rounded-lg font-medium transition-all"
            style={activeView === 'preview'
              ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
              : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
          >
            <Eye className="w-4 h-4 inline mr-2" />
            Preview
          </button>
          <button
            onClick={() => setActiveView('editor')}
            className="cs-body px-4 py-2 rounded-lg font-medium transition-all"
            style={activeView === 'editor'
              ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
              : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
          >
            <Edit3 className="w-4 h-4 inline mr-2" />
            Edit Data
          </button>
          {(data.tax1065Files?.length || 0) > 0 && (
            <button
              onClick={() => setActiveView('k1summary')}
              className="cs-body px-4 py-2 rounded-lg font-medium transition-all"
              style={activeView === 'k1summary'
                ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            >
              <Users className="w-4 h-4 inline mr-2" />
              K-1 Summary
            </button>
          )}
        </div>

        {/* Preview View */}
        {activeView === 'preview' && result && (
          <div className="space-y-6">
            {/* Enhanced Partial Year Banner with Timeline */}
            {partialYearInfo && (
              <PartialYearBanner partialYearInfo={partialYearInfo} />
            )}
            
            {/* Additional Warnings */}
            {warnings.length > 0 && !partialYearInfo && (
              <div className="rounded-xl p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                  <div>
                    <h4 className="cs-h3" style={{ color: 'var(--color-needs-human-text)' }}>Calculation Warnings</h4>
                    <ul className="cs-meta mt-1 list-disc list-inside" style={{ color: 'var(--color-needs-human-text)' }}>
                      {warnings.map((warning, idx) => (
                        <li key={idx}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {/* Causation Test Result */}
            <div className="rounded-xl p-6" style={causationPassed
              ? { background: 'transparent', border: '2px solid var(--color-verified)' }
              : { background: 'var(--color-needs-human-tint)', border: '2px solid var(--color-needs-human-tint-bd)' }}>
              <div className="flex items-center gap-4 mb-4">
                {causationPassed ? (
                  <CheckCircle className="w-12 h-12" style={{ color: 'var(--color-verified)' }} />
                ) : (
                  <XCircle className="w-12 h-12" style={{ color: 'var(--color-brand)' }} />
                )}
                <div>
                  <h3 className="cs-h2">
                    Causation Test: {causationPassed ? 'PASSED' : 'FAILED'}
                  </h3>
                  <p className="cs-meta" style={{ color: causationPassed ? 'var(--color-verified)' : 'var(--color-needs-human-text)' }}>
                    {result.causation.test_name || result.causation.test_passed || 'No test passed'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg p-3" style={{ background: 'var(--color-bg-surface)' }}>
                  <p className="cs-label">Revenue Variance</p>
                  <p className="cs-figure" style={{ fontSize: '1.125rem', color: 'var(--color-text-primary)' }}>{result.causation.variance != null ? `${(result.causation.variance * 100).toFixed(1)}%` : 'N/A'}</p>
                </div>
                <div className="rounded-lg p-3" style={{ background: 'var(--color-bg-surface)' }}>
                  <p className="cs-label">Threshold Required</p>
                  <p className="cs-figure" style={{ fontSize: '1.125rem', color: 'var(--color-text-primary)' }}>{result.causation.threshold != null ? `${(result.causation.threshold * 100).toFixed(1)}%` : 'Below prior-year month'}</p>
                </div>
                <div className="rounded-lg p-3" style={{ background: 'var(--color-bg-surface)' }}>
                  <p className="cs-label">Qualifying Periods</p>
                  <p className="cs-figure" style={{ fontSize: '1.125rem', color: 'var(--color-text-primary)' }}>{result.causation.qualifying_periods.length}</p>
                </div>
              </div>
            </div>

            {/* Final Claim */}
            <div className="rounded-xl p-6" style={{ background: 'var(--color-brand)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="cs-label" style={{ color: 'var(--color-on-brand)' }}>Final Claim Amount</p>
                  <p className="cs-figure-lg mt-1" style={{ color: 'var(--color-on-brand)' }}><FinancialValue value={result.final_claim} /></p>
                </div>
                <DollarSign className="w-16 h-16 opacity-50" style={{ color: 'var(--color-on-brand)' }} />
              </div>
            </div>

            {/* SSoT reconciliation against the saved value the War Room uses */}
            {savedClaim != null && (() => {
              const live = result.final_claim;
              const matches = Math.abs(live - savedClaim) < 0.005;
              return matches ? (
                <div
                  className="rounded-lg px-4 py-3 flex items-center gap-2"
                  style={{ background: 'transparent', border: '1px solid var(--color-verified)' }}
                >
                  <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-verified)' }} />
                  <p className="cs-meta" style={{ color: 'var(--color-verified)' }}>
                    Matches the saved report value used in the War Room (${formatCurrency(savedClaim)}).
                  </p>
                </div>
              ) : (
                <div
                  className="rounded-lg px-4 py-3 flex items-start gap-2"
                  style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                  <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
                    <strong>Saved report value (War Room): ${formatCurrency(savedClaim)}.</strong>{' '}
                    The amount above is a live preview of the current data (${formatCurrency(live)}).
                    Regenerate the report to update the value the War Room uses.
                  </p>
                </div>
              );
            })()}

            {/* BI Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-xl p-5" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  <p className="cs-label">Variable Profit Loss</p>
                </div>
                <p className="cs-figure" style={{ fontSize: '1.375rem', color: 'var(--color-text-primary)' }}>
                  <FinancialValue value={result.step1.variable_profit_loss} />
                </p>
              </div>
              <div className="rounded-xl p-5" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  <p className="cs-label">Benchmark VP</p>
                </div>
                <p className="cs-figure" style={{ fontSize: '1.375rem', color: 'var(--color-text-primary)' }}>
                  <FinancialValue value={result.step1.benchmark_vp} />
                </p>
              </div>
              <div className="rounded-xl p-5" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
                  <p className="cs-label">Coverage Period VP</p>
                </div>
                <p className="cs-figure" style={{ fontSize: '1.375rem', color: 'var(--color-text-primary)' }}>
                  <FinancialValue value={result.step1.coverage_vp} />
                </p>
              </div>
            </div>

            {/* Revenue Chart */}
            {chartData.length > 0 && (
              <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                <h4 className="cs-h3 mb-4">Revenue by Year</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="year" stroke="var(--color-text-dim)" />
                    <YAxis tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} stroke="var(--color-text-dim)" />
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="revenue" fill="var(--color-brand)" name="Revenue" />
                    <Bar dataKey="vp" fill="var(--color-verified)" name="Variable Profit" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* Editor View */}
        {activeView === 'editor' && (
          <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <h3 className="cs-h3 mb-4">Edit Financial Data</h3>
            {/*
              EditableFinancialGrid was refactored to take `reportId` +
              `financialData` + `onRecalculated`. Caller had drifted
              (was passing `data` / `onDataChange` / `onRecalculate`)
              causing a TS2322 IntrinsicAttributes mismatch and a real
              runtime no-op since the unused props weren't read. Fixed
              per archaeology pattern §5 (TS-FU3 batch 4) — newResult
              is routed into the existing onDataUpdate chain to preserve
              the original callback semantics. The component handles its
              own recalculation loading state internally.
            */}
            <EditableFinancialGrid
              reportId={reportId || claimId || ''}
              financialData={data?.financial_data}
              onRecalculated={(newResult) => {
                setEditedFinancialData(newResult);
                if (onDataUpdate) onDataUpdate({ ...data, financial_data: newResult });
              }}
            />
          </div>
        )}

        {/* K-1 Summary View */}
        {activeView === 'k1summary' && (
          <div className="rounded-xl p-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
            <h3 className="cs-h3 mb-4">K-1 Partner Summary</h3>
            <K1PartnerSummary tax1065Files={data.tax1065Files || []} />
          </div>
        )}
        
      </div>
    </WizardV2Layout>
  );
}
